import { PgliteDatabase } from "drizzle-orm/pglite";
import { contacts, customers, CustomerWithContacts, InsertCustomer, SelectContact, SelectCustomer } from "../schema";
import { eq, inArray, sql, ne } from "drizzle-orm";

class CustomersRepository {
    public add = async (db: PgliteDatabase, data: InsertCustomer) => {
        const [res] = await db.insert(customers).values(data).returning();
        return res;
    }
    public getById = async (db: PgliteDatabase, customerId: number) => {
        const res = await db.select().from(customers)
            .leftJoin(contacts, eq(customers.id, contacts.customerId))
            .where(eq(customers.id, customerId));

        return this.mapResult(res)[0];
    }
    public getAll = async (db: PgliteDatabase): Promise<SelectCustomer[]> => {
        return await db.select().from(customers).where(ne(customers.status, "archived"));
    }

    public getAllWithContacts = async (db: PgliteDatabase) => {
        const res = await db.select().from(customers)
            .leftJoin(contacts, eq(customers.id, contacts.customerId))
            .where(ne(customers.status, "archived"));
        return this.mapResult(res);
    }

    public getAllArchived = async (db: PgliteDatabase): Promise<CustomerWithContacts[]> => {
        const res = await db.select().from(customers)
            .leftJoin(contacts, eq(customers.id, contacts.customerId))
            .where(eq(customers.status, "archived"));
        return this.mapResult(res);
    }

    public getByName = async (db: PgliteDatabase, name: string) => {
        const [res] = await db.select().from(customers).leftJoin(contacts, eq(customers.name, name));
        return res;
    }

    public delete = async (db: PgliteDatabase, customerId: number) => {
        await db.delete(customers).where(eq(customers.id, customerId));
        await db.execute(sql`VACUUM customers;`);
    }
    public archive = async (db: PgliteDatabase, customerId: number) => {
        const [res] = await db.update(customers).set({ status: "archived" })
            .where(eq(customers.id, customerId)).returning();
        return res;
    }
    public archiveMany = async (db: PgliteDatabase, ids: number[]) => {
        return await db.update(customers).set({ status: "archived" })
            .where(inArray(customers.id, ids)).returning();
    }
    public restore = async (db: PgliteDatabase, id: number) => {
        return await db.update(customers).set({ status: "active" })
            .where(eq(customers.id, id)).returning();
    }
    public restoreMany = async (db: PgliteDatabase, ids: number[]) => {
        return await db.update(customers).set({ status: "active" })
            .where(inArray(customers.id, ids)).returning();
    }
    public update = async (db: PgliteDatabase, customer: InsertCustomer) => {
        const [res] = await db.update(customers).set(customer)
            .where(eq(customers.id, customer.id as number)).returning();
        return res;
    }
    public deleteMany = async (db: PgliteDatabase, ids: number[]) => {
        await db.delete(customers).where(inArray(customers.id, ids));
        await db.execute(sql`VACUUM customers;`);
    }

    private mapResult = (data: {
        contacts: SelectContact | null,
        customers: SelectCustomer
    }[]) => {
        const res = data.reduce<Record<string, CustomerWithContacts>>((acc, item) => {
            const { contacts: contact, customers: customer } = item;
            const { id } = customer;

            if (!acc[id]) {
                acc[id] = {
                    ...customer,
                    archivedContacts: [],
                    activeContacts: []
                };
            }

            if (contact) {
                if (contact.status === "active") acc[id].activeContacts.push(contact);
                if (contact.status === "archived") acc[id].archivedContacts.push(contact);
            }

            return acc;
        }, {});
        return Object.values(res);
    }

}

export const customersRepository = new CustomersRepository();